import React, { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, LayersControl, useMap, useMapEvents, ZoomControl, Polyline, AttributionControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { gql, useMutation } from "@apollo/client";
import { useStore } from "../../store";
import DroneMarker from "./DroneMarker";
import ZoneLayer from "./ZoneLayer";
import "./DroneMap.css";
import AlarmEngine from "./AlarmEngine";
import DroneLabel from "./DroneLabel";
import AlarmOverlay from "./AlarmOverlay";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const CREATE_ZONE = gql`
  mutation CreateZone($input: ZoneInput!) {
    createZone(input: $input) { id name type }
  }
`;

function sensorColor(type?: string): string {
  switch (type) {
    case "CRPC":     return "#4fc3f7";
    case "DRONE_ID": return "#aed581";
    case "RID":      return "#ffb74d";
    case "CAMERA":   return "#f48fb1";
    case "WIDEBAND": return "#ce93d8";
    default:         return "#90a4ae";
  }
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function circleGeoJSON(lat: number, lng: number, radiusM: number): object {
  const N = 64;
  const coords: [number, number][] = [];
  for (let i = 0; i <= N; i++) {
    const angle = (i / N) * 2 * Math.PI;
    const dLat = (radiusM / 111320) * Math.cos(angle);
    const dLng = (radiusM / (111320 * Math.cos((lat * Math.PI) / 180))) * Math.sin(angle);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: "Polygon", coordinates: [coords] };
}

function fmtRadius(m: number): string {
  return m >= 1000 ? ((m / 1000).toFixed(2) + " km") : (Math.round(m) + " m");
}

function MapController() {
  const { mapCenter, mapZoom } = useStore();
  const map = useMap();
  useEffect(() => {
    map.setView(mapCenter, mapZoom, { animate: true });
  }, [mapCenter, mapZoom, map]);
  return null;
}

type DrawPhase = "idle" | "pick-center" | "pick-radius";
type ZoneKind  = "WARNING" | "DEFENSE";

function SetCenterHandler({ active, onPick }: { active: boolean; onPick: (lat: number, lng: number) => void }) {
  const map = useMap();
  useMapEvents({ click(e) { if (active) onPick(e.latlng.lat, e.latlng.lng); } });
  useEffect(() => { map.getContainer().style.cursor = active ? "crosshair" : ""; }, [active, map]);
  return null;
}

function DrawZoneHandler({ phase, kind, center, onCenterPicked, onRadiusPicked, onRadiusPreview }:
  { phase: DrawPhase; kind: ZoneKind | null; center: [number,number] | null;
    onCenterPicked: (lat: number, lng: number) => void;
    onRadiusPicked: (r: number) => void;
    onRadiusPreview: (r: number) => void; }) {
  const map = useMap();
  const circleRef = useRef<L.Circle | null>(null);
  const color = kind === "DEFENSE" ? "#ef5350" : "#ffa726";

  useEffect(() => {
    map.getContainer().style.cursor = phase !== "idle" ? "crosshair" : "";
    if (phase === "idle") { circleRef.current?.remove(); circleRef.current = null; }
  }, [phase, map]);

  useMapEvents({
    click(e) {
      if (phase === "pick-center") { onCenterPicked(e.latlng.lat, e.latlng.lng); }
      else if (phase === "pick-radius" && center) {
        const r = haversineM(center[0], center[1], e.latlng.lat, e.latlng.lng);
        circleRef.current?.remove(); circleRef.current = null;
        onRadiusPicked(r);
      }
    },
    mousemove(e) {
      if (phase === "pick-radius" && center) {
        const r = haversineM(center[0], center[1], e.latlng.lat, e.latlng.lng);
        onRadiusPreview(r);
        if (circleRef.current) { circleRef.current.setRadius(r); }
        else {
          circleRef.current = L.circle(center, {
            radius: r, color, fillColor: color, fillOpacity: 0.12, weight: 2,
            dashArray: kind === "WARNING" ? "6 4" : undefined,
          }).addTo(map);
        }
      }
    },
  });
  return null;
}

interface Props { onDroneClick: (droneId: string) => void; }

export default function DroneMap({ onDroneClick }: Props) {
  const { drones, mapCenter, paths, setMapCenter } = useStore();
  const dronesArray = Array.from(drones.values());

  const [settingCenter, setSettingCenter] = useState(false);
  const [centerSaved,   setCenterSaved]   = useState(false);
  const [drawPhase,     setDrawPhase]     = useState<DrawPhase>("idle");
  const [drawKind,      setDrawKind]      = useState<ZoneKind | null>(null);
  const [zoneCenter,    setZoneCenter]    = useState<[number, number] | null>(null);
  const [radiusPreview, setRadiusPreview] = useState<number>(0);
  const [zoneMsg,       setZoneMsg]       = useState<string | null>(null);

  const [createZone, { loading: saving }] = useMutation(CREATE_ZONE, {
    refetchQueries: ["GetZones"], awaitRefetchQueries: true,
  });

  const handleCenterPick = useCallback((lat: number, lng: number) => {
    setMapCenter([lat, lng]); setSettingCenter(false);
    setCenterSaved(true); setTimeout(() => setCenterSaved(false), 2000);
  }, [setMapCenter]);

  const startDraw = (kind: ZoneKind) => {
    setSettingCenter(false); setDrawKind(kind);
    setZoneCenter(null); setRadiusPreview(0); setDrawPhase("pick-center");
  };

  const cancelDraw = () => { setDrawPhase("idle"); setDrawKind(null); setZoneCenter(null); };

  const handleZoneCenter = (lat: number, lng: number) => {
    setZoneCenter([lat, lng]); setDrawPhase("pick-radius");
  };

  const handleZoneRadius = useCallback(async (r: number) => {
    if (!zoneCenter || !drawKind) return;
    setDrawPhase("idle");
    const geo = circleGeoJSON(zoneCenter[0], zoneCenter[1], r);
    const ts  = new Date().toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" });
    const name = (drawKind === "DEFENSE" ? "Defense" : "Warning") + " Zone " + ts;
    try {
      await createZone({ variables: { input: { name, type: drawKind, geometryJson: geo } } });
      setZoneMsg("Zone " + name + " saved (r=" + fmtRadius(r) + ")");
    } catch (e: any) {
      setZoneMsg("Error: " + e.message);
    }
    setDrawKind(null); setZoneCenter(null);
    setTimeout(() => setZoneMsg(null), 3000);
  }, [zoneCenter, drawKind, createZone]);

  const hintText =
    drawPhase === "pick-center" ? ("Click map to place " + (drawKind === "DEFENSE" ? "Defense" : "Warning") + " zone center")
    : drawPhase === "pick-radius" ? ("Click to set radius  " + fmtRadius(radiusPreview))
    : settingCenter ? "Click map to save center"
    : null;

  return (
    <div className="map-container">
      <AlarmEngine />
      <MapContainer center={mapCenter} zoom={14} style={{ width: "100%", height: "100%" }} zoomControl={false} attributionControl={false}>
        <AttributionControl position="bottomleft" prefix={false} />
        <ZoomControl position="bottomright" />
        <MapController />
        <SetCenterHandler active={settingCenter && drawPhase === "idle"} onPick={handleCenterPick} />
        <DrawZoneHandler phase={drawPhase} kind={drawKind} center={zoneCenter}
          onCenterPicked={handleZoneCenter} onRadiusPicked={handleZoneRadius} onRadiusPreview={setRadiusPreview} />

        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" attribution="" maxZoom={21} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' maxZoom={19} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Terrain">
            <TileLayer url="https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}" attribution="" maxZoom={21} />
          </LayersControl.BaseLayer>
        </LayersControl>
        <ZoneLayer />
        <AlarmOverlay />
        <DroneLabel />
        {Array.from(paths.entries()).map(([droneId, positions]) => {
          if (positions.length < 2) return null;
          const drone = drones.get(droneId);
          return (<Polyline key={"tail-" + droneId} positions={positions}
            pathOptions={{ color: sensorColor(drone?.sensorType), weight: 2, opacity: 0.55, dashArray: "5 5" }} />);
        })}
        {dronesArray.map(drone => (
          <DroneMarker key={drone.droneId} drone={drone} onClick={() => onDroneClick(drone.droneId)} />
        ))}
      </MapContainer>

      <div className="map-overlay-count">
        <span className="count-badge">{dronesArray.length}</span>
        <span className="count-label">Active Drones</span>
      </div>

      <div className="map-tools">
        <button className={"map-tool-btn" + (settingCenter ? " active" : "")}
          onClick={() => { cancelDraw(); setSettingCenter(v => !v); }}
          title="Click on map to save map center">
          &#128205; Center
        </button>
        <button className={"map-tool-btn warning" + (drawPhase !== "idle" && drawKind === "WARNING" ? " active" : "")}
          onClick={() => drawPhase !== "idle" && drawKind === "WARNING" ? cancelDraw() : startDraw("WARNING")}
          disabled={saving} title="Draw Warning zone">
          &#9888;&#65039; Warning
        </button>
        <button className={"map-tool-btn defense" + (drawPhase !== "idle" && drawKind === "DEFENSE" ? " active" : "")}
          onClick={() => drawPhase !== "idle" && drawKind === "DEFENSE" ? cancelDraw() : startDraw("DEFENSE")}
          disabled={saving} title="Draw Defense zone">
          &#128737;&#65039; Defense
        </button>
      </div>

      {hintText && <div className="map-set-center-hint">{hintText}</div>}
      {centerSaved && <div className="map-set-center-saved">Center saved</div>}
      {zoneMsg && <div className={"map-set-center-saved" + (zoneMsg.startsWith("Error") ? " error" : "")}>{zoneMsg}</div>}
      {saving && <div className="map-set-center-hint">Saving zone...</div>}
    </div>
  );
}
